import React, { useState, useEffect } from'react';
import { Link, useNavigate } from'react-router-dom';
import { useTranslation } from'@/store/languageStore';
import { CrmCustomer, CrmStats } from'@/types/crm';
import { useAsyncData } from'@/hooks';
import {
PlusIcon,
MagnifyingGlassIcon,
BuildingOfficeIcon,
UserGroupIcon,
ChevronRightIcon,
ArrowLeftIcon,
} from'@heroicons/react/24/outline';
import { ROUTES } from'@/constants/routes';
import { useAuth } from '@/store/authStore';
import { usePermissionsStore } from '@/store/permissionsStore';
const statusColors: Record<string, string> = {
prospect:'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
active:'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
paused:'bg-bgBase text-textDefault',
at_risk:'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
};
const typeLabels: Record<string, string> = {
municipality:'Kommun',
region:'Region',
private:'Privat',
partner:'Partner',
car_rental:'Biluthyrare',
};
const statusLabels: Record<string, string> = {
prospect:'Prospekt',
active:'Aktiv',
paused:'Pausad',
at_risk:'Risk',
};

function PackageCell({
  name,
  modules,
  label,
  className,
}: {
  name: string;
  modules: string[];
  label: string;
  className?: string;
}) {
  const hasExtras = modules.length > 0;
  return (
    <div className={`group relative min-w-0 ${className ?? ''}`}>
      <span
        className={`block truncate ${hasExtras ? 'cursor-help underline decoration-dotted decoration-textSecondary underline-offset-2' : ''}`}
        title={!hasExtras && name !== '–' ? name : undefined}
      >
        {name}
      </span>
      {hasExtras && (
        <div className="absolute left-0 top-full z-30 hidden pt-1 group-hover:block">
          <div className="min-w-[12rem] max-w-xs rounded-lg border border-border bg-bgElevated px-3 py-2 shadow-lg">
            <div className="mb-1 text-xs font-medium text-textSecondary">{label}</div>
            <ul className="space-y-0.5 text-sm text-textDefault">
              {modules.map((moduleName) => (
                <li key={moduleName}>{moduleName}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
const CustomerList: React.FC = () => {
const { t } = useTranslation();
const navigate = useNavigate();
const { user } = useAuth();
const isCrmPartner =
  user?.is_crm_partner === true ||
  user?.is_crm_partner === 1 ||
  user?.is_crm_partner === '1';
const hasFeatureAccess = usePermissionsStore((s) => s.hasFeatureAccess);
const isAdmin =
  user?.is_global_admin === true ||
  user?.is_global_admin === 1;
const canCreateCustomer =
  isCrmPartner || isAdmin || hasFeatureAccess('CRM') || hasFeatureAccess('CRM_CUSTOMERS');
const [search, setSearch] = useState('');
const [debouncedSearch, setDebouncedSearch] = useState('');
const [statusFilter, setStatusFilter] = useState<string>('');
const [typeFilter, setTypeFilter] = useState<string>('');

// Debounce sökning – väntar 300ms efter senaste tangenttryckning
useEffect(() => {
const timer = setTimeout(() => setDebouncedSearch(search), 300);
return () => clearTimeout(timer);
}, [search]);

// Bygg params-objekt – server filtrerar och returnerar alla matchande kunder
const params: Record<string, string> = {};
if (debouncedSearch) params.search = debouncedSearch;
if (statusFilter) params.status = statusFilter;
if (typeFilter) params.customerType = typeFilter;

const { data: customers, loading } = useAsyncData<CrmCustomer[]>(
'/api/crm/customers',
{ initialData: [], params, deps: [debouncedSearch, statusFilter, typeFilter] }
);
const { data: stats } = useAsyncData<CrmStats>('/api/crm/stats');

const filteredCustomers = customers || [];
const listGrid =
  'xl:grid xl:grid-cols-[minmax(16rem,1.6fr)_9rem_12rem_10.5rem_13rem_auto] xl:items-center xl:gap-4';
const cellText = (value?: string | number | null) =>
  value === undefined || value === null || value === '' ? '–' : String(value);
return (
<div className="p-4 bg-bgContainer h-full">
{/* Header */}
<div className="flex items-center justify-between mb-6">
<div className="flex items-center gap-3">
<button onClick={() => navigate(ROUTES.CRM)} className="p-2 hover:bg-bgElevated rounded-lg transition-colors">
<ArrowLeftIcon className="w-5 h-5 text-textDefault"/>
</button>
<h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
<UserGroupIcon className="w-6 h-6 text-primary"/>
{t('crm.customers')}
</h1>
</div>
{canCreateCustomer && (
<Link
to={`${ROUTES.CRM_CUSTOMERS}/new`}
className="flex items-center gap-2 px-4 py-2 bg-bgPrimary text-white rounded-lg hover:bg-bgPrimary/90"
>
<PlusIcon className="w-5 h-5"/>
{t('crm.newCustomer') ||'Ny kund'}
</Link>
)}
</div>
{/* Stats */}
{stats && (
<div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
<div className="p-4 bg-bgElevated rounded-lg border border-border">
<div className="text-2xl font-bold text-textDefault">{stats.totalCustomers}</div>
<div className="text-sm text-textSecondary">{t('crm.totalLabel')}</div>
</div>
<div className="p-4 bg-bgElevated rounded-lg border border-border">
<div className="text-2xl font-bold text-green-600">{stats.activeCustomers}</div>
<div className="text-sm text-textSecondary">{t('crm.statusActive')}</div>
</div>
<div className="p-4 bg-bgElevated rounded-lg border border-border">
<div className="text-2xl font-bold text-yellow-600">{stats.prospects}</div>
<div className="text-sm text-textSecondary">{t('crm.statusProspect')}</div>
</div>
<div className="p-4 bg-bgElevated rounded-lg border border-border">
<div className="text-2xl font-bold text-textDefault">{stats.totalContacts}</div>
<div className="text-sm text-textSecondary">{t('crm.contactsLabel')}</div>
</div>
</div>
)}
{/* Filters and column headers */}
<div className={`flex flex-col gap-4 mb-3 px-4 ${listGrid}`}>
<div className="relative min-w-[200px]">
<MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-textSecondary"/>
<input
type="text"
value={search}
onChange={(e) => setSearch(e.target.value)}
placeholder={t('crm.searchCustomerPlaceholder')}
className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-bgBase text-textDefault"
/>
</div>
<div className="hidden xl:block text-sm font-semibold text-textDefault whitespace-nowrap">{t('crm.listColOrgNumber')}</div>
<div className="hidden xl:block text-sm font-semibold text-textDefault whitespace-nowrap">{t('crm.listColPackage')}</div>
<div className="hidden xl:block text-sm font-semibold text-textDefault whitespace-nowrap">{t('crm.listColUserCount')}</div>
<div className="hidden xl:block text-sm font-semibold text-textDefault whitespace-nowrap">{t('crm.listColContact')}</div>
<div className="flex flex-wrap gap-4 xl:justify-end">
<select
value={statusFilter}
onChange={(e) => setStatusFilter(e.target.value)}
className="px-3 py-2 border border-border rounded-lg bg-bgBase text-textDefault"
>
<option value="">{t('crm.allStatus')}</option>
<option value="prospect">{t('crm.statusProspect')}</option>
<option value="active">{t('crm.statusActive')}</option>
<option value="paused">{t('crm.statusPaused')}</option>
<option value="at_risk">{t('crm.statusAtRisk')}</option>
</select>
<select
value={typeFilter}
onChange={(e) => setTypeFilter(e.target.value)}
className="px-3 py-2 border border-border rounded-lg bg-bgBase text-textDefault"
>
<option value="">Alla typer</option>
<option value="municipality">Kommun</option>
<option value="region">Region</option>
<option value="private">Privat</option>
{!isCrmPartner && <option value="partner">Partner</option>}
<option value="car_rental">Biluthyrare</option>
</select>
</div>
</div>
{/* Customer List */}
{loading ? (
<div className="text-center py-12 text-textSecondary">Laddar...</div>
) : filteredCustomers.length === 0 ? (
<div className="text-center py-12">
<BuildingOfficeIcon className="w-12 h-12 mx-auto text-textSecondary mb-4"/>
<p className="text-textSecondary">
{(customers || []).length === 0 ? t('copy.ui.noCustomersYet') : t('copy.ui.noCustomersMatch')}
</p>
</div>
) : (
<div className="space-y-2">
{filteredCustomers.map((customer) => (
<Link
key={customer.id}
to={`${ROUTES.CRM_CUSTOMERS}/${customer.id}`}
className={`${listGrid} relative p-4 bg-bgElevated rounded-lg border border-border hover:z-20 hover:border-bgPrimary transition-colors`}
>
<div className="flex items-center gap-4 min-w-0">
<div className="w-10 h-10 shrink-0 bg-bgPrimary/10 rounded-lg flex items-center justify-center">
<BuildingOfficeIcon className="w-5 h-5 text-bgPrimary"/>
</div>
<div className="min-w-0">
<div className="font-medium text-textDefault truncate">{customer.name}</div>
<div className="text-sm text-textSecondary flex items-center gap-4 flex-wrap">
{customer.org_number && <span>{customer.org_number}</span>}
{customer.city && <span>{customer.city}</span>}
<span className="text-xs px-2 py-0.5 rounded bg-bgBase">{typeLabels[customer.customer_type]}</span>
{customer.billing_starts_on && (
<span>{t('crm.billingStartsOnShort')} {String(customer.billing_starts_on).slice(0, 10)}</span>
)}
</div>
</div>
</div>
<div className="hidden xl:block text-sm text-textDefault truncate" title={customer.org_number || undefined}>
{cellText(customer.org_number)}
</div>
<PackageCell
className="hidden xl:block text-sm text-textDefault"
name={cellText(customer.package_names)}
modules={customer.extra_modules ?? []}
label={t('crm.listExtraModules')}
/>
<div className="hidden xl:block text-sm text-textDefault">
{cellText(customer.user_count)}
</div>
<div className="hidden xl:block text-sm text-textDefault truncate" title={customer.primary_contact_name || undefined}>
{cellText(customer.primary_contact_name)}
</div>
<div className="xl:hidden mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
<span className="text-textSecondary">{t('crm.listColOrgNumber')}</span>
<span className="text-textDefault">{cellText(customer.org_number)}</span>
<span className="text-textSecondary">{t('crm.listColPackage')}</span>
<PackageCell
name={cellText(customer.package_names)}
modules={customer.extra_modules ?? []}
label={t('crm.listExtraModules')}
className="text-textDefault"
/>
<span className="text-textSecondary">{t('crm.listColUserCount')}</span>
<span className="text-textDefault">{cellText(customer.user_count)}</span>
<span className="text-textSecondary">{t('crm.listColContact')}</span>
<span className="text-textDefault">{cellText(customer.primary_contact_name)}</span>
</div>
<div className="flex items-center gap-4 xl:justify-end mt-3 xl:mt-0">
<span className={`px-2 py-1 text-xs rounded-full ${statusColors[customer.status]}`}>
{statusLabels[customer.status] || customer.status}
</span>
<ChevronRightIcon className="w-5 h-5 text-textSecondary"/>
</div>
</Link>
))}
</div>
)}
</div>
);
};
export default CustomerList;
